import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Stack,
    CircularProgress
} from '@mui/material';
import { CommonInvoiceTemplateProps, SummaryItem } from '../../../types';
import { amountToWords } from '../../utils/amountToWords';
import { toTitleCase } from '../../utils/toTitleCase';
import { request } from '../../../../../utils/request';
import { Signature } from '../../../../controlPanel/types';
import { baseUrlImage } from '../../../../../utils/apiClient';

const CommonInvoiceTemplate: React.FC<CommonInvoiceTemplateProps> = ({
    documentType = 'invoice',
    title = 'TAX INVOICE',
    subTitle,

    // Company details
    companyName = 'Company Name',
    companyLocation,
    companyAddress,
    companyPhone,
    companyEmail,
    companyGSTIN,
    companyLogo,

    // Document details
    documentNumber,
    documentDate,
    dueDate,
    dueDateLabel,
    referenceNumber,
    customFields = [],

    // Party details
    partyHeading = 'Bill To',
    partyName,
    partyLocation,
    partyAddress,
    partyPhone,
    partyEmail,
    partyGSTIN,
    partyPAN,
    amounts,

    // Table
    tableColumns = [
        { id: 'srNo', label: 'Sr. No.', align: 'center', width: 60 },
        { id: 'description', label: 'Description', align: 'left', width: '40%' },
        { id: 'quantity', label: 'Qty', align: 'right', width: 80 },
        { id: 'rate', label: 'Rate', align: 'right', width: 100 },
        {
            id: 'tax', label: 'Tax', align: 'right', width: 100, format: (value, row) => {
                // If row has taxPercentage, show it with the amount
                if (row?.taxPercentage) {
                    return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Typography variant="body2">
                                ₹{typeof value === 'number' ? value.toLocaleString() : value}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                ({row.taxPercentage}%)
                            </Typography>
                        </Box>
                    );
                }
                // If no percentage, just show the amount
                return `₹${typeof value === 'number' ? value.toLocaleString() : value}`;
            }
        },
        { id: 'amount', label: 'Amount', align: 'right', width: 120 }
    ],
    tableData = [],
    tableTotals = {},

    // Summary
    termsConditions,
    summaryItems = [],

    // Styling
    showBorder = true,
    showCompanyHeader = true,
    showPartySection = true,
    showTermsSection = true,
    showSummarySection = true,
    paperElevation = 0,

    // Custom
    customHeaderContent,
    customFooterContent,

    showLoading = false,
    showLoadingText,
    invoiceStatus
}) => {
    const [signature, setSignature] = useState<Signature>();
    // Get document type specific styling
    const getDocumentTypeColor = () => {
        switch (documentType) {
            case 'invoice': return '#1976d2';
            case 'quotation': return '#2e7d32';
            case 'sales_return': return '#ed6c02';
            case 'credit_note': return '#9c27b0';
            default: return '#1976d2';
        }
    };

    // Default summary items if not provided
    const defaultSummaryItems: SummaryItem[] = [
        { label: 'Taxable Amount', value: `₹${(tableTotals?.taxableAmount || 0).toLocaleString()}`, variant: 'body2' },
        { label: 'CGST (9%)', value: `₹${((tableTotals?.tax || 0) / 2).toLocaleString()}`, variant: 'body2' },
        { label: 'SGST (9%)', value: `₹${((tableTotals?.tax || 0) / 2).toLocaleString()}`, variant: 'body2' },
        { label: 'IGST (18%)', value: `₹${(tableTotals?.tax || 0).toLocaleString()}`, variant: 'body2' },
        { label: 'Total', value: `₹${(tableTotals?.amount || 0).toLocaleString()}`, bold: true, variant: 'body1' }
    ];

    const fetchBusinessInfo = useCallback(async () => {
        const res = await request({
            url: 'setting/signature',
            method: 'get'
        })
        if (res.success) {
            setSignature(res?.data?.signature)
        }
    }, [])
    useEffect(() => {
        fetchBusinessInfo();
    }, [fetchBusinessInfo])


    const displaySummaryItems = summaryItems.length > 0 ? summaryItems : defaultSummaryItems;

    if (showLoading) {
        return (<Box sx={{ display: 'flex', height: '50vh', justifyContent: "center", alignItems: 'center', gap: 2 }}>
            <CircularProgress />  {showLoadingText}
        </Box>)
    }

    return (
        <Paper
            elevation={paperElevation}
            sx={{
                p: 3,
                border: showBorder ? '1px solid #e0e0e0' : 'none',
                borderRadius: 1,
                width: '100%',
                maxWidth: '210mm',
                // minHeight: '297mm',
                margin: 'auto'
            }}
        >
            {/* Header Section */}
            {customHeaderContent || (
                <Box>
                    {/* Document Title */}
                    <Box textAlign="center" mb={3}>
                        <Typography
                            variant="h4"
                            fontWeight="bold"
                            sx={{ color: getDocumentTypeColor() }}
                            className='invoice-heading'
                        >
                            {title}
                        </Typography>
                        {subTitle && (
                            <Typography variant="subtitle1" color="text.secondary">
                                {subTitle}
                            </Typography>
                        )}
                    </Box>

                    {/* Company and Document Info */}
                    {showCompanyHeader && (
                        <Box display="flex" mb={3} justifyContent="space-between" sx={{ gap: 2 }} className='flex-center'>
                            {/* Company Details */}
                            <Box
                                sx={{
                                    border: '1px solid #e0e0e0',
                                    p: 2,
                                    borderRadius: 1,
                                    width: "50%"
                                }}
                            >
                                <Stack spacing={0.5}>
                                    {companyLogo && (
                                        <Box mb={1}>
                                            {companyLogo}
                                        </Box>
                                    )}
                                    <Typography variant="h4" fontWeight="bold" className='sub-heading'>
                                        {companyName}
                                    </Typography>

                                    {(companyPhone || companyEmail) && (
                                        <>
                                            <Box display="flex" gap={2}>
                                                {companyPhone && (
                                                    <Typography variant="body2">
                                                        📞 {companyPhone || "N/A"}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Box display="flex" gap={2}>
                                                {companyEmail && (
                                                    <Typography variant="body2">
                                                        {companyEmail}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </>
                                    )}

                                    {companyLocation && (
                                        <Typography variant="body2" color="text.secondary">
                                            {companyLocation}
                                        </Typography>
                                    )}
                                    {companyAddress && (
                                        <Typography variant="body2" color="text.secondary">
                                            {companyAddress}
                                        </Typography>
                                    )}
                                    {companyGSTIN && (
                                        <Typography variant="body2" color="text.secondary">
                                           GST : {companyGSTIN}
                                        </Typography>
                                    )}

                                </Stack>
                            </Box>

                            {/* Party Details */}
                            {showPartySection && (
                                <Box mb={0} width="50%" >
                                    <Box
                                        sx={{
                                            border: '1px solid #e0e0e0',
                                            p: 2,
                                            borderRadius: 1,

                                        }}
                                    >
                                        <Typography variant="subtitle1" fontWeight="bold" mb={1} className='sub-heading'>
                                            {partyHeading} :
                                        </Typography>
                                        <Stack spacing={0.5}>
                                            <Typography variant="body1" fontWeight="medium">
                                                {partyName}
                                            </Typography>
                                            {(partyPhone || partyEmail) && (
                                                <Box display="flex" gap={2}>
                                                    {partyPhone && (
                                                        <Typography variant="body2">
                                                            📞 {partyPhone}
                                                        </Typography>
                                                    )}
                                                    {partyEmail && (
                                                        <Typography variant="body2">
                                                            ✉️ {partyEmail}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )}
                                            {partyLocation && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {partyLocation}
                                                </Typography>
                                            )}
                                            {partyAddress && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {partyAddress}
                                                </Typography>
                                            )}


                                        </Stack>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>

            )}

            {/* Document Details */}
            <Box
                sx={{
                    border: '1px solid #e0e0e0',
                    p: 2,
                    borderRadius: 1,
                    flex: 1,
                    minWidth: '250px',
                    mb: 3
                }}
            >
                <Box display={"flex"} justifyContent="space-between" className='flex-center' >
                    {documentNumber && (
                        <Box display="flex" justifyContent="space-between" gap={1} className='flex-center'>
                            <Typography variant="body2" fontWeight="bold" className='text-bold'>
                                {toTitleCase(documentType)} No:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                                {documentNumber}
                            </Typography>
                        </Box>
                    )}

                    {documentDate && (
                        <Box display="flex" justifyContent="space-between" gap={1} className='flex-center'>
                            <Typography variant="body2" fontWeight="bold" className='text-bold'>
                                {toTitleCase(documentType)} Date:
                            </Typography>
                            <Typography variant="body2">
                                {documentDate}
                            </Typography>
                        </Box>
                    )}

                    {dueDate && (
                        <Box display="flex" justifyContent="space-between" gap={1} className='flex-center'>
                            <Typography variant="body2" fontWeight="bold" className='text-bold'>
                                {dueDateLabel || 'Due Date:'}
                            </Typography>
                            <Typography variant="body2">
                                {dueDate}
                            </Typography>
                        </Box>
                    )}

                    {referenceNumber && (
                        <Box display="flex" justifyContent="space-between" gap={1} className='flex-center'>
                            <Typography variant="body2" fontWeight="bold" className='text-bold'>
                                Ref No:
                            </Typography>
                            <Typography variant="body2">
                                {referenceNumber}
                            </Typography>
                        </Box>
                    )}

                </Box>
            </Box>

            {/* Items Table */}
            <Box mb={3}>
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                        border: '1px solid #e0e0e0',
                        overflowX: 'auto'
                    }}
                >
                    <Table size="small" sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                                {tableColumns.map((column) => (
                                    <TableCell
                                        key={column.id}
                                        align={column.align || 'left'}
                                        sx={{
                                            fontWeight: 'bold',
                                            minWidth: column.width,
                                            whiteSpace: 'nowrap',
                                            py: 0
                                        }}
                                    >
                                        {column.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableData.map((row, index) => {
                                const taxPercentage = row.taxPercentage;
                                return (
                                    <TableRow key={index} hover>
                                        {tableColumns.map((column) => {
                                            const value = row[column.id];

                                            // Special handling for tax column
                                            if (column.id === 'tax' && !column.format) {
                                                // Add tax percentage if not already formatted
                                                return (
                                                    <TableCell
                                                        key={`${index}-${column.id}`}
                                                        align={column.align || 'left'}
                                                        sx={{ whiteSpace: 'nowrap', py: 0 }}
                                                    >
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                            <Typography variant="body2">
                                                                ₹{typeof value === 'number' ? value.toLocaleString() : value}
                                                            </Typography>
                                                            {taxPercentage > 0 && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    ({taxPercentage}%)
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                );
                                            }

                                            return (
                                                <TableCell
                                                    key={`${index}-${column.id}`}
                                                    align={column.align || 'left'}
                                                    sx={{ whiteSpace: 'nowrap' }}
                                                >
                                                    {column.format
                                                        ? column.format(value, row)
                                                        : value
                                                    }
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}

                            {/* Totals Row - Update to show total tax with average percentage */}
                            {(tableTotals.quantity || tableTotals.taxableAmount || tableTotals.tax || tableTotals.amount) && (
                                <TableRow sx={{ bgcolor: 'action.hover', py: 0 }}>
                                    {tableColumns.map((column) => {
                                        if (column.id === 'description') {
                                            return (
                                                <TableCell key="total-label" align="left" sx={{ fontWeight: 'bold' }}>
                                                    Total
                                                </TableCell>
                                            );
                                        }

                                        if (column.id === 'quantity' && tableTotals.quantity !== undefined) {
                                            return (
                                                <TableCell key="total-qty" align="right" sx={{ fontWeight: 'bold' }}>
                                                    {tableTotals.quantity.toLocaleString()}
                                                </TableCell>
                                            );
                                        }

                                        if (column.id === 'tax' && tableTotals.tax !== undefined) {

                                            return (
                                                <TableCell key="total-tax" align="right" sx={{ fontWeight: 'bold' }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                        <Typography variant="body2" fontWeight={'bold'}>
                                                            ₹{tableTotals.tax.toLocaleString()}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            );
                                        }

                                        if (column.id === 'amount' && tableTotals.amount !== undefined) {
                                            return (
                                                <TableCell key="total-amount" align="right" sx={{ fontWeight: 'bold' }}>
                                                    ₹{tableTotals.amount.toLocaleString()}
                                                </TableCell>
                                            );
                                        }

                                        return <TableCell key={`empty-${column.id}`}></TableCell>;
                                    })}
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Terms and Summary Section */}
            <Box display="flex" sx={{ gap: 2 }} className='flex-center'>
                {/* Terms & Conditions */}
                {showTermsSection && (
                    <Box sx={{ width: showSummarySection ? '50%' : '100%' }}>
                        <Typography variant="subtitle1" fontWeight="bold" mb={1} className='sub-heading'>
                            Terms & Conditions
                        </Typography>
                        <Box
                            sx={{
                                border: '1px solid #e0e0e0',
                                p: 2,
                                borderRadius: 1,
                                minHeight: 120
                            }}
                        >
                            {Array.isArray(termsConditions) && termsConditions.map((term, index) => (
                                <Typography key={index} variant="body2" paragraph sx={{ mb: 0.5 }}>
                                    {index + 1}. {term}
                                </Typography>
                            ))}
                            {termsConditions ? (
                                <Typography variant="body2" paragraph sx={{ mb: 0.5 }}>
                                    {termsConditions}
                                </Typography>
                            ) : <Typography variant="body2" paragraph sx={{ mb: 0.5 }}>
                                All disputes are subject to Gurugram jurisdiction only
                            </Typography>}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt:2 }}>
                            {signature ? (
                                <Box
                                    component="img"
                                    src={`${baseUrlImage}${signature}`}
                                    alt="Signature"
                                    sx={{
                                        maxWidth: 200,
                                        maxHeight: 80,
                                        objectFit: 'contain',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        p: 1,
                                        bgcolor: 'background.paper'
                                    }}
                                />
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    No signature uploaded
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                {/* Summary Section */}
                {showSummarySection && (
                    <Box sx={{ width: showTermsSection ? '50%' : '100%' }}>
                        <Typography variant="subtitle1" fontWeight="bold" mb={1} className='sub-heading'>
                            Summary
                        </Typography>
                        <Box
                            sx={{
                                border: '1px solid #e0e0e0',
                                p: 2,
                                borderRadius: 1
                            }}

                        >
                            <Stack spacing={1}>
                                {displaySummaryItems.map((item, index) => (
                                    <Box
                                        key={index}
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        className='flex-center'
                                    >
                                        <Typography
                                            variant={item.variant || 'body2'}
                                            color="text.secondary"
                                            sx={{ fontWeight: item.bold ? 'bold' : 'normal' }}
                                        >
                                            {item.label}
                                        </Typography>
                                        <Typography
                                            variant={item.variant || 'body2'}
                                            sx={{
                                                fontWeight: item.bold ? 'bold' : 'medium',
                                                color: item.bold ? getDocumentTypeColor() : 'inherit'
                                            }}
                                        >
                                            {typeof item.value === 'number'
                                                ? `₹${item.value.toLocaleString()}`
                                                : item.value}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>

                            {customFields && (
                                <Box mt={2} pt={2} borderTop="1px dashed #e0e0e0">
                                    {customFields.map((field, index) => (
                                        <Box
                                            key={index}
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            className='flex-center'
                                        >
                                            <Typography variant="body2" color="text.secondary">
                                                {field.label}
                                            </Typography>
                                            <Typography variant="body2" align={field.align || 'right'}>
                                                {field.value}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            {/* Total in words */}
                            {amounts?.totalAmount && (
                                <Box mt={2} pt={2} borderTop="1px dashed #e0e0e0">
                                    <Typography variant="body2"  >
                                        <b>Total Amount ( In Words ): </b> <br />  <i>{amountToWords(amounts?.totalAmount || 0)}</i>
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

export default CommonInvoiceTemplate;